/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useRef } from 'react';
import SockJS from 'sockjs-client';
import { ActivationState, Client } from '@stomp/stompjs';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { isFeatureEnabled, ToggleableFeature } from '../../shared/utils/featuresUtils';
import { AUTHORIZATION_HEADER_KEY, buildSubscriptionPath, WS_CONNECTION_PARAMS } from './useWebsockets.helper';
import { websocketPath } from '../../settings';
import { getAccessToken } from '../../redux/features/auth/auth.service';
import { getUserOrganizationId } from '../../redux/features/organization/organization.helpers';

/**
 * Hook for websocket communication that connects to the notification-service
 * and subscribes to the given topics when the component calling it is mounted
 * and unsubscribes and disconnects when the component is unmounted.
 *
 * Use example:
 *   useWebsockets({
 *    topics: ['submission-update', 'another-topic-useful-for-component', ...],
 *    onMessageReceived: (message, destination) => {
 *      const topic = destination.split('/').slice(-1)[0];
 *      switch (topic) {
 *        case 'topic 1':
 *          ...
 *          break;
 *        case 'topic X':
 *          ...
 *          break;
 *       }
 *    },
 *   });
 */
export default function useWebsockets(connectionProps) {
  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);
  const accessToken = getAccessToken();

  useEffect(() => {
    if (
      globalConfiguration.ready &&
      accessToken &&
      isFeatureEnabled(globalConfiguration.resource, ToggleableFeature.WS_NOTIFICATIONS)
    ) {
      getUserOrganizationId()
        .then(userTenantId => connectToSocket(websocketPath, userTenantId, accessToken))
        .catch(() => console.log('Error extracting OrganizationId'));
      return () => disconnect();
    }
    return () => {};
  }, [accessToken, globalConfiguration.ready]);

  // extract or default the connection props
  const {
    onConnect = () => {},
    onDisconnect = () => {},
    onWebSocketError = () => {},
    onMessageReceived = (msg, destination) => {
      logMessage(`message: ${msg} from destination: ${destination}`);
    },
    topics = [],
    debug = false,
  } = connectionProps;

  // internal references
  const client = useRef(null);
  const connected = useRef(false);
  const subscriptions = useRef(new Map());
  const topicsRef = useRef(topics);
  const onMessageReceivedCallback = useRef(onMessageReceived);

  const initStompClient = (url, jwtAccessToken) => {
    return new Client({
      connectHeaders: {
        [AUTHORIZATION_HEADER_KEY]: `Bearer ${jwtAccessToken}`,
      },
      ...WS_CONNECTION_PARAMS,
      webSocketFactory: () => new SockJS(url),
      onWebSocketError,
      debug: logMessage,
    });
  };

  const connectToSocket = (url, tenantId, jwtAccessToken) => {
    client.current = initStompClient(url, jwtAccessToken);

    if (client.current.state === ActivationState.INACTIVE) {
      client.current.activate();

      client.current.onConnect = () => {
        connected.current = true;

        topicsRef.current.forEach((topic) => {
          subscribeToTopic(topic, tenantId);
        });

        onConnect();
      };
    }
  };

  const disconnect = () => {
    if (connected.current) {
      for (const topic of subscriptions.current.keys()) {
        unsubscribeFromTopic(topic);
      }

      client.current.deactivate(() => {
        cleanUp();
        onDisconnect();
        logMessage('Stomp client is successfully disconnected!');
      });
    }
  };

  const subscribeToTopic = (topic, tenantId) => {
    if (!subscriptions.current.has(topic)) {
      const topicPath = buildSubscriptionPath(tenantId, topic);
      const subscription = client.current.subscribe(topicPath, (jsonmessage) => {
        const onMessageCallback = onMessageReceivedCallback.current;
        onMessageCallback(JSON.parse(jsonmessage?.body), jsonmessage.headers.destination);
      });

      subscriptions.current.set(topic, subscription);
    }
  };

  const unsubscribeFromTopic = (topic) => {
    const subscription = subscriptions.current.get(topic);
    subscription.unsubscribe();
    subscriptions.current.delete(topic);
  };

  const cleanUp = () => {
    connected.current = false;
    client.current = null;
    subscriptions.current.clear();
  };

  const logMessage = (message) => {
    if (debug) {
      // eslint-disable-next-line no-console
      console.log(message);
    }
  };
}
